// frontend/src/pages/SuperAdmin/SubscriptionSettings.js
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiSettings, FiSave, FiPlus, FiTrash2, FiDollarSign, FiInfo, FiToggleLeft, FiToggleRight, FiMail, FiEdit2, FiX, FiCheck } from 'react-icons/fi';
import {
  getAdminSubscriptionPlans, updateSubscriptionPlans, deleteSubscriptionPlan,
  getOfferEmails, addOfferEmail, updateOfferEmail, removeOfferEmail,
} from '../../services/api';
import styles from './SubscriptionSettings.module.css';

const formatPrice = (p) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(p || 0);

const TIER_LABELS = { basic: 'Basic', standard: 'Standard', premium: 'Premium' };
const TIER_BADGE_CLASS = {
  basic: styles.tierBadgeBasic,
  standard: styles.tierBadgeStandard,
  premium: styles.tierBadgePremium,
};

const SubscriptionSettings = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [offerEmails, setOfferEmails] = useState([]);
  const [offersLoading, setOffersLoading] = useState(true);
  const [newOfferEmail, setNewOfferEmail] = useState('');
  const [newOfferPlanId, setNewOfferPlanId] = useState('');
  const [newOfferOffline, setNewOfferOffline] = useState(false);
  const [addingOffer, setAddingOffer] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editPlanId, setEditPlanId] = useState('');

  useEffect(() => {
    getAdminSubscriptionPlans()
      .then((res) => setPlans(res.plans.map((p) => ({ ...p }))))
      .catch(() => toast.error('Failed to load subscription plans'))
      .finally(() => setLoading(false));

    getOfferEmails()
      .then((res) => setOfferEmails(res))
      .catch(() => toast.error('Failed to load offer emails'))
      .finally(() => setOffersLoading(false));
  }, []);

  const handleChange = (idx, field, value) => {
    setPlans((prev) =>
      prev.map((p, i) =>
        i === idx
          ? { ...p, [field]: field === 'price' || field === 'years' ? Number(value) : value }
          : p
      )
    );
  };

  const handleAddPlan = () => {
    const maxYears = plans.reduce((max, p) => Math.max(max, p.years || 0), 0);
    const newYears = maxYears + 1;
    setPlans((prev) => [
      ...prev,
      { years: newYears, label: `${newYears} Year${newYears > 1 ? 's' : ''}`, price: 0, isSpecial: true, tier: 'basic' },
    ]);
  };

  const handleRemovePlan = (idx) => {
    if (plans.length <= 1) { toast.error('At least one plan is required'); return; }
    setPlans((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteSpecialPlan = async (plan, idx) => {
    if (!window.confirm(`Delete special plan "${plan.label}"? This cannot be undone.`)) return;
    try {
      await deleteSubscriptionPlan(plan.id);
      setPlans((prev) => prev.filter((_, i) => i !== idx));
      toast.success('Subscription plan deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete plan');
    }
  };

  const handleSave = async () => {
    const invalid = plans.find((p) => !p.years || !p.label || p.price < 0);
    if (invalid) { toast.error('Please fill in all plan fields with valid values'); return; }
    setSaving(true);
    try {
      const sorted = [...plans].sort((a, b) => a.years - b.years);
      const res = await updateSubscriptionPlans(sorted);
      setPlans(res.plans.map((p) => ({ ...p })));
      toast.success('Subscription plans updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update plans');
    } finally {
      setSaving(false);
    }
  };

  const specialPlans = plans.filter((p) => p.isSpecial && p.id);

  const handleAddOffer = async () => {
    if (!newOfferEmail) { toast.error('Email is required'); return; }
    if (!newOfferPlanId && !newOfferOffline) { toast.error('Select a special plan or enable offline payment (or both)'); return; }
    setAddingOffer(true);
    try {
      const created = await addOfferEmail({
        email: newOfferEmail,
        ...(newOfferPlanId ? { planItemId: Number(newOfferPlanId) } : {}),
        allowOfflinePayment: newOfferOffline,
      });
      setOfferEmails((prev) => [created, ...prev]);
      setNewOfferEmail('');
      setNewOfferPlanId('');
      setNewOfferOffline(false);
      toast.success('Offer email added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add offer email');
    } finally {
      setAddingOffer(false);
    }
  };

  const handleToggleOffline = async (o) => {
    const next = !o.allowOfflinePayment;
    try {
      await updateOfferEmail(o.id, { allowOfflinePayment: next });
      setOfferEmails((prev) => prev.map((x) => x.id === o.id ? { ...x, allowOfflinePayment: next } : x));
      toast.success(`Offline payment ${next ? 'enabled' : 'disabled'} for ${o.email}`);
    } catch {
      toast.error('Failed to update offline setting');
    }
  };

  const startEdit = (o) => {
    setEditingId(o.id);
    setEditPlanId(o.planItem?.id ? String(o.planItem.id) : '');
  };

  const handleSaveEdit = async (o) => {
    try {
      const updated = await updateOfferEmail(o.id, {
        allowOfflinePayment: o.allowOfflinePayment,
        planItemId: editPlanId ? Number(editPlanId) : null,
      });
      setOfferEmails((prev) => prev.map((x) => x.id === o.id ? { ...x, planItem: updated.planItem ?? null } : x));
      setEditingId(null);
      toast.success('Plan updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update plan');
    }
  };

  const handleRemoveOffer = async (id) => {
    try {
      await removeOfferEmail(id);
      setOfferEmails((prev) => prev.filter((o) => o.id !== id));
      toast.success('Offer email removed');
    } catch {
      toast.error('Failed to remove offer email');
    }
  };

  return (
    <div className={styles.container}>
      <div className="page-header">
        <h2><FiSettings size={20} style={{ marginRight: 8 }} />Subscription Settings</h2>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginTop: 4 }}>
          Manage subscription plan durations, pricing, and special offers
        </p>
      </div>

      {/* Plans card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <FiDollarSign size={16} style={{ marginRight: 6 }} />
            Subscription Plans
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleAddPlan}>
            <FiPlus size={14} style={{ marginRight: 4 }} /> Add Plan
          </button>
        </div>

        {loading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : (
          <>
            <div className={styles.tableHead}>
              <div>Duration (Years)</div>
              <div>Label</div>
              <div>Tier</div>
              <div>Price (₹)</div>
              <div>Per Year</div>
              <div>Special Offer</div>
              <div />
            </div>

            <div className={styles.planRows}>
              {plans.map((plan, idx) => {
                const isCorePlan = !plan.isSpecial && !!plan.id;
                return (
                  <div key={idx} className={`${styles.planRow} ${plan.isSpecial ? styles.planRowSpecial : ''}`}>
                    <div className={styles.yearsCell}>
                      {isCorePlan ? (
                        <span>{plan.years} yr{plan.years !== 1 ? 's' : ''}</span>
                      ) : (
                        <>
                          <input
                            className={styles.input}
                            type="number" min="1" max="99"
                            value={plan.years}
                            onChange={(e) => handleChange(idx, 'years', e.target.value)}
                          />
                          <span className={styles.yrLabel}>yr{plan.years !== 1 ? 's' : ''}</span>
                        </>
                      )}
                    </div>
                    <div>
                      {isCorePlan ? (
                        <span>{plan.label}</span>
                      ) : (
                        <input
                          className={styles.input}
                          value={plan.label}
                          onChange={(e) => handleChange(idx, 'label', e.target.value)}
                          placeholder="e.g. 1 Year"
                        />
                      )}
                    </div>
                    <div className={styles.tierCell}>
                      {isCorePlan ? (
                        <span
                          className={`${styles.tierBadge} ${TIER_BADGE_CLASS[plan.tier] || styles.tierBadgeBasic}`}
                        >
                          {TIER_LABELS[plan.tier] || plan.tier}
                        </span>
                      ) : (
                        <select
                          className={styles.input}
                          value={plan.tier || 'basic'}
                          onChange={(e) => handleChange(idx, 'tier', e.target.value)}
                        >
                          {Object.entries(TIER_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div>
                      <div className={styles.priceInput}>
                        <span className={styles.currencySymbol}>₹</span>
                        <input
                          className={`${styles.input} ${styles.priceField}`}
                          type="number" min="0"
                          value={plan.price}
                          onChange={(e) => handleChange(idx, 'price', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className={styles.perYear}>
                      {plan.price > 0 ? formatPrice(Math.round(plan.price / (plan.years || 1))) : '—'} / yr
                    </div>
                    <div className={styles.specialCell}>
                      {isCorePlan ? (
                        <span>—</span>
                      ) : (
                        <label className={styles.specialToggle}>
                          <input
                            type="checkbox"
                            checked={!!plan.isSpecial}
                            onChange={(e) => handleChange(idx, 'isSpecial', e.target.checked)}
                          />
                          <span>{plan.isSpecial ? 'Yes' : 'No'}</span>
                        </label>
                      )}
                    </div>
                    <div className={styles.removeCell}>
                      {isCorePlan ? (
                        <span>—</span>
                      ) : (
                        <button
                          className={styles.removeBtn}
                          onClick={() => (plan.id ? handleDeleteSpecialPlan(plan, idx) : handleRemovePlan(idx))}
                          title="Remove plan"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.cardFooter}>
              <div className={styles.infoNote}>
                <FiInfo size={13} style={{ marginRight: 5, flexShrink: 0 }} />
                Price changes apply to new registrations only. Special offer plans are hidden from regular users.
              </div>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                <FiSave size={14} style={{ marginRight: 6 }} />
                {saving ? 'Saving…' : 'Save Plans'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Offer Emails card */}
      <div className={styles.card} style={{ marginTop: 24 }}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>
            <FiMail size={16} style={{ marginRight: 6 }} />
            Special Offer Emails
          </div>
        </div>

        <div className={styles.infoNote} style={{ padding: '10px 16px', borderBottom: '1px solid var(--color-border)' }}>
          <FiInfo size={13} style={{ marginRight: 5, flexShrink: 0 }} />
          Enable "Offline Payment" per email to let that admin pay via offline transfer instead of Razorpay.
        </div>

        <div className={styles.offerAddRow}>
          <input
            className={styles.input}
            style={{ flex: 2 }}
            type="email"
            placeholder="admin@derasar.com"
            value={newOfferEmail}
            onChange={(e) => setNewOfferEmail(e.target.value)}
          />
          <select
            className={styles.input}
            style={{ flex: 1 }}
            value={newOfferPlanId}
            onChange={(e) => setNewOfferPlanId(e.target.value)}
          >
            <option value="">Select special plan…</option>
            {specialPlans.map((p) => (
              <option key={p.id} value={p.id}>{p.label} — {formatPrice(p.price)}</option>
            ))}
          </select>
          <label className={styles.offerOfflineCheck}>
            <input
              type="checkbox"
              checked={newOfferOffline}
              onChange={(e) => setNewOfferOffline(e.target.checked)}
            />
            <span>Offline</span>
          </label>
          <button className="btn btn-primary btn-sm" onClick={handleAddOffer} disabled={addingOffer}>
            <FiPlus size={14} style={{ marginRight: 4 }} /> Add
          </button>
        </div>

        {offersLoading ? (
          <div className="loading"><div className="spinner" /></div>
        ) : offerEmails.length === 0 ? (
          <p className={styles.offerEmptyNote}>No special offer emails configured yet.</p>
        ) : (
          <>
            <div className={styles.offerListHead}>
              <span>Email</span>
              <span>Plan</span>
              <span>Offline</span>
              <span />
              <span />
            </div>
            <div className={styles.offerList}>
              {offerEmails.map((o) => (
                <div key={o.id} className={`${styles.offerRow} ${editingId === o.id ? styles.offerRowEditing : ''}`}>
                  <span className={styles.offerEmail}>{o.email}</span>

                  {/* Plan cell — normal or edit mode */}
                  {editingId === o.id ? (
                    <select
                      className={styles.input}
                      value={editPlanId}
                      onChange={(e) => setEditPlanId(e.target.value)}
                      autoFocus
                    >
                      <option value="">No plan</option>
                      {specialPlans.map((p) => (
                        <option key={p.id} value={p.id}>{p.label} — {formatPrice(p.price)}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={styles.offerPlan}>
                      {o.planItem ? `${o.planItem.label} — ${formatPrice(o.planItem.price)}` : '—'}
                    </span>
                  )}

                  {/* Offline toggle */}
                  <button
                    className={styles.offlineToggleBtn}
                    onClick={() => handleToggleOffline(o)}
                    title={o.allowOfflinePayment ? 'Disable offline payment' : 'Enable offline payment'}
                  >
                    {o.allowOfflinePayment
                      ? <FiToggleRight size={20} style={{ color: 'var(--color-primary)' }} />
                      : <FiToggleLeft size={20} style={{ color: 'var(--color-text-muted)' }} />}
                  </button>

                  {/* Edit / Save+Cancel */}
                  {editingId === o.id ? (
                    <span className={styles.offerEditActions}>
                      <button className={styles.saveBtn} onClick={() => handleSaveEdit(o)} title="Save">
                        <FiCheck size={14} />
                      </button>
                      <button className={styles.removeBtn} onClick={() => setEditingId(null)} title="Cancel">
                        <FiX size={14} />
                      </button>
                    </span>
                  ) : (
                    <button className={styles.editBtn} onClick={() => startEdit(o)} title="Edit plan">
                      <FiEdit2 size={13} />
                    </button>
                  )}

                  <button className={styles.removeBtn} onClick={() => handleRemoveOffer(o.id)} title="Remove">
                    <FiTrash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSettings;
